'use client';

import React, { useState, useEffect } from 'react';
import { Tv2, Eye, EyeOff, Loader2, CheckCircle2, XCircle, Save, AlertTriangle, Plus, Trash2, ChevronDown, ChevronRight,  } from 'lucide-react';
import { toast } from 'sonner';
import type { SonarrInstance, PayloadField } from '@/lib/types';
import { configStore } from '@/lib/configStore';
import PayloadFieldsEditor from './PayloadFieldsEditor';

type ConnectionStatus = 'idle' | 'testing' | 'ok' | 'error';

interface InstanceFormState {
  name: string;
  url: string;
  apiKey: string;
  qualityProfile: string;
  payloadFields: PayloadField[];
}

interface InstanceCardProps {
  instance: SonarrInstance;
  onSave: (id: string, data: InstanceFormState) => void;
  onDelete: (id: string) => void;
}

function InstanceCard({ instance, onSave, onDelete }: InstanceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [connStatus, setConnStatus] = useState<ConnectionStatus>('idle');
  const [connMessage, setConnMessage] = useState('');
  const [lastTested, setLastTested] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [form, setForm] = useState<InstanceFormState>({
    name: instance.name,
    url: instance.url,
    apiKey: instance.apiKey,
    qualityProfile: instance.qualityProfile,
    payloadFields: instance.payloadFields,
  });

  const statusClass: Record<ConnectionStatus, string> = {
    idle: 'connection-idle',
    testing: 'connection-idle',
    ok: 'connection-ok',
    error: 'connection-error',
  };

  const testConnection = async () => {
    if (!form.url || !form.apiKey) {
      toast.error('Enter a URL and API key before testing');
      return;
    }
    setConnStatus('testing');
    setConnMessage('');
    await new Promise((r) => setTimeout(r, 1400));
    const ok = form.url.includes('localhost') || form.url.includes('192.168');
    if (ok) {
      setConnStatus('ok');
      setConnMessage('Connected · Sonarr v4.0.9 · 312 series');
      setLastTested(new Date().toISOString());
    } else {
      setConnStatus('error');
      setConnMessage('Unable to reach Sonarr — check URL and API key');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setIsSaving(true);
    await new Promise((r) => setTimeout(r, 600));
    setIsSaving(false);
    onSave(instance.id, form);
    toast.success(`"${form.name}" saved`);
  };

  return (
    <div className="card-elevated overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-3 p-4">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'color-mix(in srgb, var(--sonarr) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--sonarr) 30%, transparent)' }}
        >
          <Tv2 size={14} className="text-sonarr" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{instance.name}</p>
          <p className="text-2xs text-muted-foreground font-mono-data truncate">{instance.url || 'No URL set'}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {connStatus !== 'idle' && (
            <span className={`flex items-center gap-1 text-2xs font-medium px-2 py-0.5 rounded-full ${statusClass[connStatus]}`}>
              {connStatus === 'testing' && <Loader2 size={10} className="animate-spin" />}
              {connStatus === 'ok' && <CheckCircle2 size={10} />}
              {connStatus === 'error' && <XCircle size={10} />}
              {connStatus === 'testing' ? 'Testing' : connStatus === 'ok' ? 'OK' : 'Error'}
            </span>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-ghost p-2 rounded-lg"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {deleteConfirm ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onDelete(instance.id)}
                className="px-2 py-1 text-2xs font-semibold rounded-md bg-danger text-white"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="btn-ghost px-2 py-1 text-2xs"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDeleteConfirm(true)}
              className="btn-ghost p-2 rounded-lg text-muted-foreground hover:text-danger"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Expanded form */}
      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-4 flex flex-col gap-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Instance Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Sonarr 4K"
              className="input-base w-full px-3 py-2 text-sm"
            />
          </div>

          {/* URL */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Sonarr URL</label>
            <p className="text-2xs text-muted-foreground mb-1.5">Base URL including port if non-standard</p>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder="http://localhost:8989"
              className="input-base w-full px-3 py-2 text-sm"
            />
          </div>

          {/* API Key */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">API Key</label>
            <p className="text-2xs text-muted-foreground mb-1.5">Settings → General → Security</p>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={form.apiKey}
                onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                placeholder="32-character API key"
                className="input-base w-full px-3 py-2 pr-10 text-sm font-mono-data"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Quality Profile */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Default Quality Profile</label>
            <input
              type="text"
              value={form.qualityProfile}
              onChange={(e) => setForm((f) => ({ ...f, qualityProfile: e.target.value }))}
              placeholder="e.g. HD-1080p"
              className="input-base w-full px-3 py-2 text-sm"
            />
          </div>

          {/* Payload Fields */}
          <PayloadFieldsEditor
            fields={form.payloadFields}
            onChange={(fields) => setForm((f) => ({ ...f, payloadFields: fields }))}
          />

          {/* Connection test result */}
          {connMessage && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-xs ${connStatus === 'ok' ? 'connection-ok' : 'connection-error'}`}>
              {connStatus === 'ok' ? <CheckCircle2 size={13} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />}
              <div>
                <p className="font-medium">{connMessage}</p>
                {lastTested && (
                  <p className="text-2xs mt-0.5 opacity-70 font-mono-data">
                    Tested at {new Date(lastTested).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={testConnection}
              disabled={connStatus === 'testing'}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium btn-ghost border border-border disabled:opacity-50"
            >
              {connStatus === 'testing' ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Test Connection
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold btn-primary disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SonarrPanel() {
  const [instances, setInstances] = useState<SonarrInstance[]>([]);

  useEffect(() => {
    setInstances(configStore.getSonarrInstances());
  }, []);

  const addInstance = () => {
    const newInst: SonarrInstance = {
      id: `sonarr-${Date.now()}`,
      name: `Sonarr Instance ${instances.length + 1}`,
      url: 'http://localhost:8989',
      apiKey: '',
      qualityProfile: 'HD-1080p',
      payloadFields: configStore.makeDefaultPayloadFieldsSonarr(),
      createdAt: new Date().toISOString(),
    };
    const updated = [...instances, newInst];
    setInstances(updated);
    configStore.saveSonarrInstances(updated);
    toast.success('New Sonarr instance added');
  };

  const saveInstance = (id: string, data: InstanceFormState) => {
    const updated = instances.map((inst) =>
      inst.id === id ? { ...inst, ...data } : inst
    );
    setInstances(updated);
    configStore.saveSonarrInstances(updated);
  };

  const deleteInstance = (id: string) => {
    if (instances.length === 1) {
      toast.error('At least one Sonarr instance is required');
      return;
    }
    const updated = instances.filter((inst) => inst.id !== id);
    setInstances(updated);
    configStore.saveSonarrInstances(updated);
    toast.success('Instance removed');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'color-mix(in srgb, var(--sonarr) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--sonarr) 30%, transparent)' }}
          >
            <Tv2 size={18} className="text-sonarr" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Sonarr</h2>
            <p className="text-xs text-muted-foreground">
              {instances.length} instance{instances.length !== 1 ? 's' : ''} configured
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={addInstance}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium btn-primary"
        >
          <Plus size={14} />
          Add Instance
        </button>
      </div>

      {/* Instances */}
      {instances.length === 0 ? (
        <div className="card-elevated flex flex-col items-center justify-center py-14 text-center">
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
            <Tv2 size={22} className="text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">No Sonarr instances</h3>
          <p className="text-xs text-muted-foreground max-w-xs">Add a Sonarr instance to pull series into the media library.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {instances.map((inst) => (
            <InstanceCard
              key={inst.id}
              instance={inst}
              onSave={saveInstance}
              onDelete={deleteInstance}
            />
          ))}
        </div>
      )}

      <div className="p-3 rounded-lg connection-idle flex items-start gap-2">
        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
        <p className="text-2xs">
          Each instance pulls its own series list. Items in the media library are tagged with their source instance.
        </p>
      </div>
    </div>
  );
}